import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useOptionalCustomerBasketAdapter } from "@shared-core/data/CustomerBasketAdapterContext";
import {
  NO_BASKET_CAPABILITIES,
  type CustomerBasketCapabilities,
} from "@shared-core/data/customer-basket-adapter";
import { basketCapabilities } from "@shared-core/domain/customer-basket-capabilities";
import type { CustomerBasketView } from "@shared-core/data/customer-basket-types";
import type { ListQueryState } from "@shared-core/domain/useListQuery";
import { usePortalViewer } from "../PortalViewerContext";

// One read of the person's basket shared by everything in the portal layout that shows it: the nav item's
// count, the basket page, the checkout page and the item page's Add to basket. A write anywhere calls
// `refresh()` and every one of them re-reads, keeping what is on screen (no flash of "Loading") until the new
// read lands. Mounted per viewer (the layout keys it on the viewer), so a different person or customer starts
// from a fresh read.
//
// Absent where the build has no basket adapter, the person cannot order, or the server says ordering is not
// open to them yet (the read answers null): `useCustomerBasket()` returns null there, and every entry point
// (nav item, Add to basket, Order again) hides itself.
//
// `capabilities` is one flag per group of change (changes, files, checkout, reorder): on only where the adapter
// implements the group AND the basket read names that group's action URL, which the server does only while its
// routes are mounted (`basketCapabilities`). A group that is off draws no control; the basket is shown as read.

interface CustomerBasketContextValue {
  state: ListQueryState<CustomerBasketView>;
  refresh: () => void;
  /** Which groups of change are on, from the adapter and the read's URLs; a control of a group that is off is not drawn. */
  capabilities: CustomerBasketCapabilities;
  /** Where `capabilities.changes` is off: the one sentence that says why (the adapter's), shown on the basket page. */
  readOnlyNote: string | null;
}

const CustomerBasketContext = createContext<CustomerBasketContextValue | null>(
  null,
);

export function CustomerBasketProvider({ children }: { children: ReactNode }) {
  const adapter = useOptionalCustomerBasketAdapter();
  const { sessionState } = usePortalViewer();
  const canOrder =
    sessionState.status === "success" && sessionState.data.can_order_by_card;
  const [state, setState] = useState<ListQueryState<CustomerBasketView>>({
    status: "loading",
  });
  // The read answered "not open to this person": nothing basket-shaped is drawn anywhere.
  const [isNotOpen, setIsNotOpen] = useState(false);
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (!adapter || !canOrder) return;
    let cancelled = false;
    adapter
      .getBasket()
      .then((data) => {
        if (cancelled) return;
        setIsNotOpen(data === null);
        if (data !== null) setState({ status: "success", data });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: "error",
            message: error instanceof Error ? error.message : String(error),
            error,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, canOrder, version]);

  const readView = state.status === "success" ? state.data : null;
  const capabilities = useMemo(
    () =>
      basketCapabilities(
        adapter?.capabilities ?? NO_BASKET_CAPABILITIES,
        readView,
      ),
    [adapter, readView],
  );
  const readOnlyNote = adapter?.readOnlyNote ?? null;
  const value = useMemo(
    () => ({ state, refresh, capabilities, readOnlyNote }),
    [state, refresh, capabilities, readOnlyNote],
  );

  // The provider element is always there (a different element type per state would remount everything
  // beneath it, the open account menu included); only what it offers changes.
  const isOpen = adapter !== null && canOrder && !isNotOpen;
  return (
    <CustomerBasketContext.Provider value={isOpen ? value : null}>
      {children}
    </CustomerBasketContext.Provider>
  );
}

/** The shared basket read, or null where this build or this person has no basket. */
export function useCustomerBasket(): CustomerBasketContextValue | null {
  return useContext(CustomerBasketContext);
}
